<template>
  <div class="main-layout">
    <!-- 顶部导航栏 -->
    <header class="header" v-if="!isFullscreenPage">
      <!-- 左侧：系统名称和Logo -->
      <div class="header-left">
        <img :src="systemLogo" alt="系统Logo" class="logo">
        <h1 class="system-name">{{ systemName }}</h1>
      </div>

      <!-- 右侧：用户信息和操作 -->
      <div class="header-right">
        <!-- 站内信 -->
        <div class="message-icon" @click="toggleMessageDropdown" ref="messageDropdownContainer">
          <i class="el-icon-bell"></i>
          <span v-if="unreadCount > 0" class="unread-badge">{{ unreadCount }}</span>

          <!-- 消息下拉框 -->
          <div class="message-dropdown" v-if="showMessageDropdown">
            <div class="dropdown-header">
              <span>消息通知</span>
              <span @click="markAllAsRead" class="mark-all">全部标为已读</span>
            </div>
            <div class="message-list">
              <div v-for="msg in messages" :key="msg.id" class="message-item" :class="{ 'unread': msg.isRead === 0 }">
                <div class="message-title">{{ msg.title }}</div>
                <div class="message-time">{{ formatTime(msg.createTime) }}</div>
              </div>
              <div v-if="messages.length === 0" class="no-message">暂无消息</div>
            </div>
          </div>
        </div>

        <!-- 用户信息 -->
        <div class="user-info" @click="toggleUserDropdown" ref="userDropdownContainer">
          <img :src="userAvatar" alt="用户头像" class="user-avatar">
          <span class="user-name">{{ userName }}</span>
          <i class="el-icon-arrow-down"></i>

          <!-- 用户操作下拉框 -->
          <div class="user-dropdown" v-if="showUserDropdown">
            <div class="dropdown-item" @click="goToProfile">个人中心</div>
            <div class="dropdown-item" @click="logout">退出登录</div>
          </div>
        </div>
      </div>
    </header>

    <!-- 主内容区 -->
    <div class="main-content">
      <!-- 左侧菜单 -->
      <aside class="sidebar" v-if="!isFullscreenPage">
        <LeftMenu />
      </aside>

      <!-- 右侧内容 -->
      <div class="content" :class="{ 'fullscreen-content': isFullscreenPage }">
        <router-view />
      </div>
    </div>
  </div>
</template>

<script>
import systemApi from '../../api/system'
import userApi from '../../api/user'
import messageApi from '../../api/message'
import LeftMenu from '../../components/LeftMenu.vue'

export default {
  name: 'MainLayout',
  components: {
    LeftMenu  // 注册组件
  },
  data() {
    return {
      // 系统信息
      systemName: '',
      systemLogo: require('../../assets/logo.png'), // 默认logo

      // 用户信息
      userName: '',
      userAvatar: require('../../assets/avatar-default.png'), // 默认头像

      // 消息相关
      messages: [],
      unreadCount: 0,
      showMessageDropdown: false,

      // 用户下拉框
      showUserDropdown: false
    }
  },
  computed: {
    // 判断当前路由是否为全屏页面
    isFullscreenPage() {
      // 读取路由元信息中的fullscreen标记
      return this.$route.meta?.fullscreen || false;
    }
  },
  created() {
    this.loadSystemConfig()
    this.loadCurrentUser()
    this.loadMessages()
  },
  methods: {
    // 加载系统配置
    loadSystemConfig() {
      systemApi.getSystemConfig().then(response => {
        if (response.success) {
          this.systemName = response.data.systemName || '数字靶场管理系统'
          if (response.data.systemLogo) {
            this.systemLogo = response.data.systemLogo
          }
        }
      })
    },

    // 加载当前用户信息
    loadCurrentUser() {
      userApi.getCurrentUser().then(response => {
        if (response.success) {
          this.userName = response.data.name
          if (response.data.avatar) {
            this.userAvatar = response.data.avatar
          }
        }
      })
    },

    // 加载消息
    loadMessages() {
      messageApi.getMessages().then(response => {
        if (response.success) {
          this.messages = response.data
        }
      })

      // 获取未读消息数量
      messageApi.getUnreadCount().then(response => {
        if (response.success) {
          this.unreadCount = response.data
        }
      })
    },

    // 格式化时间
    formatTime(time) {
      const date = new Date(time)
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
    },

    // 标记所有消息为已读
    markAllAsRead() {
      const unreadMessages = this.messages.filter(msg => msg.isRead === 0)
      unreadMessages.forEach(msg => {
        messageApi.markAsRead(msg.id).then(() => {
          this.unreadCount = 0
          this.loadMessages()
        })
      })
    },

    // 切换消息下拉框显示
    toggleMessageDropdown() {
      this.showMessageDropdown = !this.showMessageDropdown
      this.showUserDropdown = false
    },

    // 切换用户下拉框显示
    toggleUserDropdown() {
      this.showUserDropdown = !this.showUserDropdown
      this.showMessageDropdown = false
    },

    // 个人中心
    goToProfile() {
      this.showUserDropdown = false
      this.$router.push('/profile')
    },

    // 退出登录
    logout() {
      this.showUserDropdown = false
      sessionStorage.removeItem('token')
      this.$router.push('/login')
      this.$message.success('已退出登录')
    }

  },
  // 点击页面其他地方关闭下拉框
  mounted() {
    // 定义事件处理函数（便于销毁时移除）
    this.documentClickHandler = (e) => {
      // 获取两个下拉框的DOM容器
      const messageEl = this.$refs.messageDropdownContainer;
      const userEl = this.$refs.userDropdownContainer;
      // 判断点击是否在消息下拉框内部
      const isInMessage = messageEl && messageEl.contains(e.target);
      // 判断点击是否在用户下拉框内部
      const isInUser = userEl && userEl.contains(e.target);
      // 点击外部时，关闭对应下拉框
      if (!isInMessage) {
        this.showMessageDropdown = false;
      }
      if (!isInUser) {
        this.showUserDropdown = false;
      }
    };

    document.addEventListener('click', this.documentClickHandler)
  },

  // 组件销毁时移除事件监听，避免内存泄漏
  beforeDestroy() {
    document.removeEventListener('click', this.documentClickHandler);
  }
}
</script>

<style scoped>
.main-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

/* 顶部导航 */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 60px;
  background-color: #2c3e50 !important;
  color: white;
  padding: 0 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-left {
  display: flex;
  align-items: center;
}

.logo {
  width: 40px;
  height: 40px;
  margin-right: 10px;
}

.system-name {
  font-size: 18px;
  margin: 0;
}

.header-right {
  display: flex;
  align-items: center;
}

/* 消息图标 */
.message-icon {
  position: relative;
  margin-right: 20px;
  cursor: pointer;
  font-size: 20px;
}

.unread-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: #e74c3c;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* 消息下拉框 */
.message-dropdown {
  position: absolute;
  top: 30px;
  right: 0;
  width: 300px;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 100;
  color: #333;
}

.dropdown-header {
  display: flex;
  justify-content: space-between;
  padding: 10px 15px;
  border-bottom: 1px solid #eee;
}

.mark-all {
  color: #42b983;
  font-size: 12px;
  cursor: pointer;
}

.message-list {
  max-height: 300px;
  overflow-y: auto;
}

.message-item {
  padding: 10px 15px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.message-item:hover {
  background-color: #f5f5f5;
}

.message-item.unread {
  background-color: #f8f9fa;
  font-weight: bold;
}

.message-title {
  margin-bottom: 5px;
}

.message-time {
  font-size: 12px;
  color: #999;
}

.no-message {
  padding: 20px;
  text-align: center;
  color: #999;
}

/* 用户信息 */
.user-info {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  margin-right: 10px;
}

.user-name {
  margin-right: 5px;
}

/* 用户下拉框 */
.user-dropdown {
  position: absolute;
  top: 50px;
  right: 20px;
  width: 150px;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 100;
  color: #333;
}

.dropdown-item {
  padding: 10px 15px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.dropdown-item:hover {
  background-color: #f5f5f5;
}

/* 主内容区 */
.main-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* 左侧菜单 */
.sidebar {
  width: 200px;
  background-color: #34495e;
  color: white;
  height: 100%;
  overflow-y: auto;
  text-align: center;
}

/* 右侧内容 */
.content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background-color: #ecf0f1;
}

/* 全屏内容区专属样式 */
.fullscreen-content {
  /* 强制占满整个视口 */
  width: 100vw !important;
  height: 100vh !important;
  /* 清除上级布局的影响 */
  flex: 1 !important;
  margin: 0 !important;
  padding: 0 !important;
  /* 重置背景色 */
  background-color: #fff !important;
  /* 保持内容可滚动 */
  overflow: auto !important;
}
</style>